import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, UserPlus, Combine, EyeOff, ChevronDown, ChevronRight, Sparkles, Search, X, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Input } from "./ui/input";
import { useApiBase } from "../context/ApiBaseContext";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";

/**
 * P2.5 — "Needs Review" tray (renamed from "Possible Characters").
 *
 * Workflow-critical surface for low-confidence parser candidates. It
 * sits above the Cast list on the Cast hub, auto-expanded when there
 * is at least one pending row.
 *
 *   - Each row carries: name + reason chip + scene + episode + source
 *     snippet + actions (Add to Cast main/crowd · Merge with… ·
 *     Ignore).
 *   - Episode filter + scene-ascending ordering keep noisy
 *     multi-episode lists reviewable.
 *   - MergePicker is a simple searchable list of existing characters —
 *     no fuzzy auto-suggest, per spec.
 */
function _reasonChip(reason) {
    const map = {
        "mentioned in action": { label: "Mentioned in Action", className: "border-violet-400/30 text-violet-200" },
        "possible object": { label: "Possible Object", className: "border-amber-400/30 text-amber-200" },
        "possibly not a character": { label: "Possibly Not a Character", className: "border-rose-400/30 text-rose-200" },
    };
    return map[reason] || { label: reason, className: "border-white/10 text-neutral-300" };
}

function MergePicker({ characters, onPick, onCancel, busy }) {
    const [q, setQ] = useState("");
    const filtered = (characters || [])
        .filter((c) =>
            q ? (c.name || "").toLowerCase().includes(q.toLowerCase()) : true,
        )
        .slice(0, 30);
    return (
        <div
            data-testid="merge-picker"
            className="mt-2 rounded-md border border-white/10 bg-secondary/40 p-2"
        >
            <div className="flex items-center gap-2">
                <Search size={14} className="text-neutral-500" />
                <Input
                    autoFocus
                    placeholder="Search existing character…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="h-8 text-[12px]"
                    data-testid="merge-picker-input"
                />
                <button
                    type="button"
                    onClick={onCancel}
                    className="text-neutral-500 hover:text-neutral-300"
                    data-testid="merge-picker-cancel"
                >
                    <X size={14} />
                </button>
            </div>
            <div className="mt-1 max-h-40 overflow-y-auto">
                {filtered.length === 0 ? (
                    <div className="text-[11px] text-neutral-500 px-2 py-1">
                        No matching characters.
                    </div>
                ) : (
                    filtered.map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            disabled={busy}
                            onClick={() => onPick(c)}
                            className="w-full text-left px-2 py-1.5 rounded text-[12px] text-neutral-200 hover:bg-card/60 disabled:opacity-50 flex items-center justify-between"
                            data-testid={`merge-picker-option-${c.id}`}
                        >
                            <span>{c.name}</span>
                            {c.team === "crowd" ? (
                                <span className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                                    crowd
                                </span>
                            ) : null}
                        </button>
                    ))
                )}
            </div>
        </div>
    );
}

function _scene_sort_key(row) {
    const raw = row?.scene_number;
    const n = typeof raw === "number" ? raw : parseInt(String(raw || ""), 10);
    if (Number.isFinite(n)) return [0, n];
    // Items without a parsable scene number sort to the end, in their
    // original order (created_at).
    return [1, row?.created_at || ""];
}

export default function PossibleCharactersSection({
    projectId,
    rows = [],
    characters = [],
    onMutated,
    canManage = true,
    defaultOpen = false,
}) {
    const apiBase = useApiBase();
    // P2.5 — auto-expand when there's at least one pending row. The
    // parent controls the initial `defaultOpen` (true on Cast hub
    // landing); local state owns subsequent toggles.
    const [expanded, setExpanded] = useState(Boolean(defaultOpen));
    const [busyId, setBusyId] = useState(null);
    const [mergingId, setMergingId] = useState(null);
    const [episodeFilter, setEpisodeFilter] = useState("all");
    // Iter-153 — confirmation modal state. Holds the action that's
    // pending user confirmation. `null` when no modal is open.
    // Shape: { kind: "merge"|"ignore", row, body? , target? }
    const [pendingConfirm, setPendingConfirm] = useState(null);
    // Iter-149.2 tidy — keep the tray auto-expanded whenever there
    // are unresolved review items. If the user explicitly collapses
    // it (via the toggle) we honour that for the current row set,
    // but a fresh batch of rows (e.g. after a re-parse) re-opens
    // the tray so they don't get missed.
    const prevRowsCount = useRef(rows.length);
    useEffect(() => {
        const wasEmpty = prevRowsCount.current === 0;
        const hasItemsNow = rows.length > 0;
        if (wasEmpty && hasItemsNow) {
            setExpanded(true);
        }
        prevRowsCount.current = rows.length;
    }, [rows.length]);

    const episodes = useMemo(() => {
        const set = new Set();
        for (const r of rows || []) {
            if (r && r.episode != null && r.episode !== "") {
                set.add(String(r.episode));
            }
        }
        // Iter-149.3 — keep the user's currently-selected episode in
        // the chip rail even if every row for it has been resolved,
        // so the user stays inside their review context instead of
        // silently snapping back to "All".
        if (episodeFilter && episodeFilter !== "all") {
            set.add(String(episodeFilter));
        }
        return Array.from(set).sort((a, b) => {
            const ai = parseInt(a, 10);
            const bi = parseInt(b, 10);
            if (Number.isFinite(ai) && Number.isFinite(bi)) return ai - bi;
            return a.localeCompare(b);
        });
    }, [rows, episodeFilter]);

    const filteredRows = useMemo(() => {
        let list = rows || [];
        if (episodeFilter !== "all") {
            list = list.filter(
                (r) => String(r?.episode ?? "") === String(episodeFilter),
            );
        }
        // Scene-ascending order within the visible set.
        return [...list].sort((a, b) => {
            const [ag, an] = _scene_sort_key(a);
            const [bg, bn] = _scene_sort_key(b);
            if (ag !== bg) return ag - bg;
            if (typeof an === "number" && typeof bn === "number")
                return an - bn;
            return String(an).localeCompare(String(bn));
        });
    }, [rows, episodeFilter]);

    if (!rows.length) return null;

    const callMutation = async (row, kind, body) => {
        setBusyId(`${row.id}:${kind}`);
        try {
            await api.post(
                `${apiBase}/${projectId}/possible-characters/${row.id}/${kind}`,
                body || {},
            );
            const verb =
                kind === "add"
                    ? "added to Cast"
                    : kind === "merge"
                      ? "merged"
                      : "dismissed";
            toast.success(`${row.name} ${verb}`);
            if (onMutated) await onMutated();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusyId(null);
            setMergingId(null);
        }
    };

    return (
        <section
            data-testid="needs-review-section"
            // Calm-UI accent: a single 2px amber left-border + faint
            // amber background tint. NO loud red banner.
            // Iter-152 — section renamed visually to "Cast Review".
            // testid `needs-review-section` retained for stable
            // backwards-compat with existing tests.
            className="rounded-xl border border-white/10 border-l-2 border-l-amber-400/60 bg-amber-500/[0.04] backdrop-blur-md"
        >
            <button
                type="button"
                data-testid="needs-review-toggle"
                onClick={() => setExpanded((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 text-left"
            >
                <div className="flex items-center gap-2 min-w-0">
                    {expanded ? (
                        <ChevronDown size={14} className="text-neutral-400" />
                    ) : (
                        <ChevronRight size={14} className="text-neutral-400" />
                    )}
                    <AlertCircle size={14} className="text-amber-300 shrink-0" />
                    <span
                        data-testid="needs-review-label"
                        className="text-[13px] text-neutral-100 font-medium truncate"
                    >
                        {/* Iter-155B.1 — Plain-English count integrated
                            into the sentence. The old layout was a
                            number-only badge + uppercase "REVIEW" mini
                            label, which on small viewports visually
                            merged into "4R"/"4Q" and confused laypeople. */}
                        {rows.length === 1
                            ? "1 possible character found"
                            : `${rows.length} possible characters found`}
                    </span>
                    {/* The numeric badge testid is retained for tests
                        that pin the unread count, but it's now visually
                        a count chip rather than a badge-plus-label. */}
                    <span
                        data-testid="needs-review-count"
                        className="sr-only"
                    >
                        {rows.length}
                    </span>
                </div>
                <span
                    data-testid="needs-review-cta"
                    className="text-[11px] text-amber-200 shrink-0 font-medium"
                >
                    {expanded ? "Hide" : "Review"}
                </span>
            </button>
            {expanded ? (
                <div className="border-t border-white/5 px-4 py-3 space-y-3">
                    <p className="text-[11px] text-neutral-400 leading-relaxed">
                        These are names the script mentions that we couldn&apos;t
                        match to your cast. Review when convenient — nothing
                        is blocked. Your decisions are remembered across
                        future script changes.
                    </p>

                    {episodes.length > 1 || (episodeFilter && episodeFilter !== "all") ? (
                        <div
                            data-testid="needs-review-episode-filter"
                            className="flex items-center gap-1.5 flex-wrap"
                        >
                            <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mr-1">
                                Episode
                            </span>
                            <button
                                type="button"
                                onClick={() => setEpisodeFilter("all")}
                                data-testid="needs-review-episode-all"
                                aria-pressed={episodeFilter === "all"}
                                className={`tap h-7 px-2 rounded-full text-[11px] border ${
                                    episodeFilter === "all"
                                        ? "bg-amber-500/20 border-amber-400/40 text-amber-100"
                                        : "bg-secondary/40 border-white/10 text-neutral-300"
                                }`}
                            >
                                All · {rows.length}
                            </button>
                            {episodes.map((ep) => {
                                const count = rows.filter(
                                    (r) => String(r?.episode ?? "") === ep,
                                ).length;
                                return (
                                    <button
                                        key={ep}
                                        type="button"
                                        onClick={() => setEpisodeFilter(ep)}
                                        data-testid={`needs-review-episode-${ep}`}
                                        aria-pressed={episodeFilter === ep}
                                        className={`tap h-7 px-2.5 rounded-full text-[11px] border ${
                                            episodeFilter === ep
                                                ? "bg-amber-500/20 border-amber-400/40 text-amber-100"
                                                : "bg-secondary/40 border-white/10 text-neutral-300"
                                        }`}
                                    >
                                        EP {ep} · {count}
                                    </button>
                                );
                            })}
                        </div>
                    ) : null}

                    {filteredRows.length === 0 ? (
                        <div className="text-[11px] text-neutral-500 px-1 py-2">
                            {episodeFilter && episodeFilter !== "all"
                                ? `All caught up in EP ${episodeFilter}.`
                                : "Nothing to review."}
                        </div>
                    ) : null}

                    {filteredRows.map((row) => {
                        const chip = _reasonChip(row.reason);
                        const isBusy = (k) => busyId === `${row.id}:${k}`;
                        const anyBusy =
                            isBusy("add") || isBusy("merge") || isBusy("ignore");
                        return (
                            <div
                                key={row.id}
                                data-testid={`possible-row-${row.id}`}
                                className="rounded-lg border border-white/5 bg-secondary/30 p-3"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span
                                                data-testid={`possible-row-${row.id}-name`}
                                                className="text-[13px] text-neutral-100 font-medium"
                                            >
                                                {row.name}
                                            </span>
                                            <span
                                                className={`text-[10px] uppercase tracking-[0.15em] border rounded-full px-1.5 py-0.5 ${chip.className}`}
                                            >
                                                {chip.label}
                                            </span>
                                            {typeof row.scene_number === "number" ||
                                            (typeof row.scene_number === "string" &&
                                                row.scene_number.trim() !== "") ? (
                                                <span className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                                                    · scene {row.scene_number}
                                                </span>
                                            ) : null}
                                            {row.episode ? (
                                                <span className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                                                    · EP {row.episode}
                                                </span>
                                            ) : null}
                                        </div>
                                        {row.source_snippet ? (
                                            <p
                                                data-testid={`possible-row-${row.id}-snippet`}
                                                className="mt-1.5 text-[11px] text-neutral-400 leading-relaxed italic"
                                            >
                                                "{row.source_snippet}"
                                            </p>
                                        ) : null}
                                    </div>
                                </div>
                                {canManage ? (
                                    <>
                                        <div className="mt-2.5 flex flex-wrap gap-2">
                                            <button
                                                type="button"
                                                disabled={anyBusy}
                                                onClick={() =>
                                                    callMutation(row, "add", {
                                                        team: "main",
                                                    })
                                                }
                                                data-testid={`possible-row-${row.id}-add-main`}
                                                className="tap inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-white/10 bg-card/60 text-[11px] text-neutral-200 hover:bg-card disabled:opacity-50"
                                            >
                                                {isBusy("add") ? (
                                                    <Loader2
                                                        size={11}
                                                        className="animate-spin"
                                                    />
                                                ) : (
                                                    <UserPlus size={11} />
                                                )}
                                                Add to Cast
                                            </button>
                                            <button
                                                type="button"
                                                disabled={anyBusy}
                                                onClick={() =>
                                                    callMutation(row, "add", {
                                                        team: "crowd",
                                                    })
                                                }
                                                data-testid={`possible-row-${row.id}-add-crowd`}
                                                className="tap inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-white/10 bg-card/60 text-[11px] text-neutral-200 hover:bg-card disabled:opacity-50"
                                            >
                                                Add as Crowd
                                            </button>
                                            <button
                                                type="button"
                                                disabled={anyBusy}
                                                onClick={() =>
                                                    setMergingId(
                                                        mergingId === row.id
                                                            ? null
                                                            : row.id,
                                                    )
                                                }
                                                data-testid={`possible-row-${row.id}-merge`}
                                                className="tap inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-white/10 bg-card/60 text-[11px] text-neutral-200 hover:bg-card disabled:opacity-50"
                                            >
                                                <Combine size={11} />
                                                Merge with…
                                            </button>
                                            <button
                                                type="button"
                                                disabled={anyBusy}
                                                onClick={() =>
                                                    setPendingConfirm({
                                                        kind: "ignore",
                                                        row,
                                                    })
                                                }
                                                data-testid={`possible-row-${row.id}-ignore`}
                                                title="Dismiss — kept in a hidden archive, never deleted"
                                                className="tap inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-white/10 bg-card/60 text-[11px] text-neutral-400 hover:bg-card disabled:opacity-50"
                                            >
                                                {isBusy("ignore") ? (
                                                    <Loader2
                                                        size={11}
                                                        className="animate-spin"
                                                    />
                                                ) : (
                                                    <EyeOff size={11} />
                                                )}
                                                Dismiss
                                            </button>
                                        </div>
                                        {mergingId === row.id ? (
                                            <MergePicker
                                                characters={characters}
                                                busy={isBusy("merge")}
                                                onCancel={() =>
                                                    setMergingId(null)
                                                }
                                                onPick={(c) => {
                                                    // Iter-153 — gate merge behind explicit
                                                    // confirmation modal.
                                                    setMergingId(null);
                                                    setPendingConfirm({
                                                        kind: "merge",
                                                        row,
                                                        target: c,
                                                        body: { character_id: c.id },
                                                    });
                                                }}
                                            />
                                        ) : null}
                                    </>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            ) : null}

            {/* Iter-153 — confirmation modal driver for Merge + Dismiss.
                Sits inside the same <section> so it inherits provider
                contexts. Calm copy; safe touch targets. */}
            <AlertDialog
                open={Boolean(pendingConfirm)}
                onOpenChange={(open) => {
                    if (!open) setPendingConfirm(null);
                }}
            >
                <AlertDialogContent
                    data-testid="possible-confirm-dialog"
                    className="max-w-[92vw] sm:max-w-md"
                >
                    {pendingConfirm?.kind === "merge" ? (
                        <>
                            <AlertDialogHeader>
                                <AlertDialogTitle>
                                    Confirm character merge
                                </AlertDialogTitle>
                                <AlertDialogDescription className="text-[13px] leading-relaxed">
                                    You are about to merge{" "}
                                    <span className="font-medium text-neutral-100">
                                        {pendingConfirm?.row?.name}
                                    </span>{" "}
                                    into{" "}
                                    <span className="font-medium text-neutral-100">
                                        {pendingConfirm?.target?.name}
                                    </span>
                                    . This will combine their cast records
                                    and scene relationships.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter className="flex-row gap-2">
                                <AlertDialogCancel
                                    data-testid="possible-confirm-cancel"
                                    className="flex-1"
                                >
                                    Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                    data-testid="possible-confirm-merge"
                                    className="flex-1"
                                    onClick={() => {
                                        const p = pendingConfirm;
                                        setPendingConfirm(null);
                                        if (p) callMutation(p.row, "merge", p.body);
                                    }}
                                >
                                    Merge Characters
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </>
                    ) : pendingConfirm?.kind === "ignore" ? (
                        <>
                            <AlertDialogHeader>
                                <AlertDialogTitle>
                                    Remove review item?
                                </AlertDialogTitle>
                                <AlertDialogDescription className="text-[13px] leading-relaxed">
                                    Are you sure you want to remove{" "}
                                    <span className="font-medium text-neutral-100">
                                        {pendingConfirm?.row?.name}
                                    </span>{" "}
                                    from Cast Review? Dismissed items stay
                                    in a hidden archive — never deleted.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter className="flex-row gap-2">
                                <AlertDialogCancel
                                    data-testid="possible-confirm-cancel"
                                    className="flex-1"
                                >
                                    Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                    data-testid="possible-confirm-remove"
                                    className="flex-1"
                                    onClick={() => {
                                        const p = pendingConfirm;
                                        setPendingConfirm(null);
                                        if (p) callMutation(p.row, "ignore");
                                    }}
                                >
                                    Remove
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </>
                    ) : null}
                </AlertDialogContent>
            </AlertDialog>
        </section>
    );
}

// Decorative icon kept around for downstream callers that imported it
// from the old name.
export { Sparkles as PossibleCharactersIcon };
